import { type InfiniteData, type QueryClient, useInfiniteQuery, useQuery } from "@tanstack/react-query"
import type { UIMessage } from "ai"
import { useCallback, useMemo, useState } from "react"
import type { ChatTail, OlderCount } from "./agent-chat"
import { type ArchiveIndex, fetchArchiveIndex, fetchArchivePage } from "./chat-archive"

/**
 * Decision 0160: an agent's chat loads its newest messages first and older ones on scroll-up.
 *
 * - `fetchChatTail`: the newest page (`GET …/chat-page`: `{messages, before, olderLive, archived, wrote}`, cloud
 *   `agents/chat-page.ts`). A cloud without the route (mid-deploy) gets `/get-messages`, the whole live window, as
 *   before.
 * - `useOlderMessages`: what is older than the messages the chat holds, as one TanStack infinite query. Each page goes
 *   further back: the live window first (`…/chat-page?before=<id>`), then the archived pages (decision 0137, newest
 *   first). Nothing is asked until the reader scrolls up to the top (or a search hit needs an older message).
 */

/** The newest messages a chat asks for first (the cloud's `FIRST_PAGE_MESSAGES`). */
export const FIRST_PAGE = 40
/** An older page (the cloud's `OLDER_PAGE_MESSAGES`). */
export const OLDER_PAGE = 100

const BODY_PATH = "/api/agents/agent-body/"

/** The body path of an agent (`/api/agents/agent-body/<workspaceId>.<agentId>`). */
export const bodyPath = (name: string): string => `${BODY_PATH}${encodeURIComponent(name)}`

/** `…/chat-page` under a body's URL (a path, or the socket's HTTP URL with its query, which is kept). */
export const chatPageUrl = (
  base: string,
  options: { readonly before?: string; readonly limit?: number } = {},
): string => {
  const url = new URL(base, globalThis.location?.href ?? "http://localhost")
  url.pathname += "/chat-page"
  if (options.before !== undefined) url.searchParams.set("before", options.before)
  if (options.limit !== undefined) url.searchParams.set("limit", String(options.limit))
  return url.href
}

class HttpError extends Error {
  constructor(
    readonly status: number,
    what: string,
  ) {
    super(`${what} ${status}`)
  }
}

const getJson = async (fetchFn: typeof fetch, url: string, what: string): Promise<unknown> => {
  const r = await fetchFn(url, { credentials: "same-origin" })
  if (!r.ok) throw new HttpError(r.status, what)
  return r.json()
}

interface PageJson {
  readonly messages: ReadonlyArray<UIMessage>
  readonly before?: unknown
  readonly olderLive?: unknown
  readonly archived?: unknown
  readonly wrote?: unknown
}

const isPage = (raw: unknown): raw is PageJson =>
  !!raw && typeof raw === "object" && Array.isArray((raw as PageJson).messages)

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null)

/**
 * The newest page of an agent's chat. `base` is the body's HTTP URL (the socket's `getHttpUrl()`) or its path. Falls
 * back to the whole live window (`/get-messages`) when the cloud has no `chat-page` or answers something else; an
 * access error (401, 403) is final.
 */
export const fetchChatTail = async (
  base: string,
  fetchFn: typeof fetch = (...args) => fetch(...args),
): Promise<ChatTail> => {
  try {
    const raw = await getJson(fetchFn, chatPageUrl(base, { limit: FIRST_PAGE }), "chat-page")
    if (isPage(raw))
      return {
        messages: raw.messages,
        older: { live: num(raw.olderLive) ?? 0, archived: num(raw.archived) },
        ...(typeof raw.wrote === "boolean" ? { viewerWrote: raw.wrote } : {}),
      }
  } catch (e) {
    // only a body without the route (it answers 404) is asked the old way; any other failure is the chat's failure
    if (!(e instanceof HttpError && e.status === 404)) throw e
  }
  const url = new URL(base, globalThis.location?.href ?? "http://localhost")
  url.pathname += "/get-messages"
  const all = await getJson(fetchFn, url.href, "get-messages")
  if (!Array.isArray(all)) throw new Error("get-messages: not a list")
  return { messages: all as ReadonlyArray<UIMessage> }
}

/** Where the next older page starts: before a live message, or the n-th archived page counted from the newest. */
export type OlderCursor =
  | { readonly kind: "live"; readonly before: string }
  | { readonly kind: "archive"; readonly nth: number }

export interface OlderPage {
  readonly messages: ReadonlyArray<UIMessage>
  readonly next: OlderCursor | null
  /** Messages older than this page (null: not known). */
  readonly remaining: number | null
}

/** The first older page's cursor for a chat that holds `messages` (`older` absent: it holds the whole live window). */
export const firstCursor = (
  messages: ReadonlyArray<UIMessage>,
  older: OlderCount | undefined,
): OlderCursor | null => {
  const first = messages[0]
  if (older !== undefined && older.live > 0 && first) return { kind: "live", before: first.id }
  if (older !== undefined && older.archived === 0) return null
  return { kind: "archive", nth: 0 }
}

/** One older page of the agent `<workspaceId>.<agentId>`. `index` answers the archive's page list. */
export const fetchOlderPage = async (
  workspaceId: string,
  agentId: string,
  cursor: OlderCursor,
  index: () => Promise<ArchiveIndex>,
  fetchFn: typeof fetch = (...args) => fetch(...args),
): Promise<OlderPage> => {
  if (cursor.kind === "live") {
    const url = chatPageUrl(bodyPath(`${workspaceId}.${agentId}`), {
      before: cursor.before,
      limit: OLDER_PAGE,
    })
    const raw = await getJson(fetchFn, url, "chat-page")
    if (!isPage(raw)) throw new Error("chat-page: not a page")
    const archived = num(raw.archived)
    const next: OlderCursor | null =
      typeof raw.before === "string"
        ? { kind: "live", before: raw.before }
        : archived === 0
          ? null
          : { kind: "archive", nth: 0 }
    return {
      messages: raw.messages,
      next,
      remaining: archived === null ? null : (num(raw.olderLive) ?? 0) + archived,
    }
  }
  const list = await index()
  const at = list.pages.length - 1 - cursor.nth
  const info = list.pages[at]
  if (!info) return { messages: [], next: null, remaining: 0 }
  const messages = await fetchArchivePage(workspaceId, agentId, info.page, fetchFn)
  const rest = list.pages.slice(0, at)
  return {
    messages,
    next: rest.length > 0 ? { kind: "archive", nth: cursor.nth + 1 } : null,
    remaining: rest.reduce((n, p) => n + p.count, 0),
  }
}

const indexQuery = (workspaceId: string, agentId: string) => ({
  queryKey: ["chat-archive-index", workspaceId, agentId] as const,
  queryFn: () => fetchArchiveIndex(workspaceId, agentId),
  staleTime: 60_000,
  retry: 1,
})

const cursorKey = (c: OlderCursor | null): string =>
  c === null ? "none" : c.kind === "live" ? `live:${c.before}` : `archive:${c.nth}`

/** The older messages loaded so far (oldest first), how many are left, and the action that loads the next page. */
export interface OlderMessages {
  readonly older: ReadonlyArray<UIMessage>
  readonly remaining: number
  readonly loading: boolean
  readonly failed: boolean
  readonly loadEarlier: () => void
}

/** The messages older than a page's list: the pages are newest first, each one older than the one before. */
export const olderOf = (pages: ReadonlyArray<OlderPage>): ReadonlyArray<UIMessage> => {
  const out: UIMessage[] = []
  for (let k = pages.length - 1; k >= 0; k--) out.push(...(pages[k]?.messages ?? []))
  return out
}

/** How many messages are older than what the chat shows. */
export const remainingOf = (
  start: OlderCursor | null,
  pages: ReadonlyArray<OlderPage>,
  older: OlderCount | undefined,
  index: ArchiveIndex | undefined,
): number => {
  if (start === null) return 0
  const last = pages.at(-1)
  if (last) return last.next === null ? 0 : (last.remaining ?? index?.messages ?? 0)
  return (older?.live ?? 0) + (older?.archived ?? index?.messages ?? 0)
}

/**
 * What is older than the messages a chat holds (`chat.ready` once its stored transcript is in): one infinite query per
 * chat start (the chat's first message), enabled the first time `loadEarlier` runs.
 */
export function useOlderMessages(
  workspaceId: string,
  agentId: string,
  chat: {
    readonly messages: ReadonlyArray<UIMessage>
    readonly older?: OlderCount | undefined
    readonly ready: boolean
  },
): OlderMessages {
  const start = chat.ready ? firstCursor(chat.messages, chat.older) : null
  const startKey = cursorKey(start)
  const [wanted, setWanted] = useState<string | null>(null)
  // the archive's size is not in the first page (an older cloud, or a cut while open): ask for its page list
  const index = useQuery({
    ...indexQuery(workspaceId, agentId),
    enabled: chat.ready && start !== null && (chat.older === undefined || chat.older.archived === null),
  })
  const query = useInfiniteQuery<
    OlderPage,
    Error,
    InfiniteData<OlderPage, OlderCursor>,
    ReadonlyArray<string>,
    OlderCursor
  >({
    queryKey: ["chat-older", workspaceId, agentId, startKey],
    queryFn: ({ pageParam, client }: { pageParam: OlderCursor; client: QueryClient }) =>
      fetchOlderPage(workspaceId, agentId, pageParam, () =>
        client.fetchQuery(indexQuery(workspaceId, agentId)),
      ),
    initialPageParam: start ?? { kind: "archive", nth: 0 },
    getNextPageParam: (last) => last.next ?? undefined,
    enabled: start !== null && wanted === startKey,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60_000,
    retry: 1,
  })
  const pages = query.data?.pages
  const older = useMemo(() => olderOf(pages ?? []), [pages])
  const remaining = remainingOf(start, pages ?? [], chat.older, index.data)
  const { fetchNextPage, hasNextPage, isFetching, isError, refetch } = query
  const loaded = pages?.length ?? 0
  const loadEarlier = useCallback(() => {
    if (start === null || isFetching) return
    if (wanted !== startKey) setWanted(startKey)
    else if (loaded === 0 && isError) void refetch()
    else if (hasNextPage) void fetchNextPage()
  }, [start, startKey, wanted, isFetching, loaded, isError, refetch, hasNextPage, fetchNextPage])
  return {
    older,
    remaining,
    loading: isFetching,
    failed: isError || query.isFetchNextPageError,
    loadEarlier,
  }
}
